import { useCallback, useEffect, useMemo } from 'react'
import { Popup } from '../../../components/Popup'
import { Forms } from '../../../components/Forms'
import { Button, BUTTON_TYPES } from '../../../components/Button'
import { Typography } from '../../../components/Typography'
import { useCRUDUserPopupProviderContext } from '../../../pages/CRUDUser/Provider/CRUDUserPopupProvider'
import { BTN_TXT } from '../../../constants'
import {
  formatReassignDataToForm,
  formatReassignDataToRequest
} from '../ManageAssignmentWithCoOrRFPHSPresidentPopup/helper'
import { useCRUDUserContext } from '../../../pages/CRUDUser/Provider/CRUDUserProvider'
import { ManageAssignmentPopupWithoutItemsProps } from './types'
import { TCCRelatedItemReassign } from '../types'
import './styles.scss'

export const ManageAssignmentPopupWithoutItems = ({
  selectedCC,
  userName
}: ManageAssignmentPopupWithoutItemsProps) => {
  const {
    assignWithoutRelatedItemsPopup: {
      actions: { close },
      state: { visible, title }
    }
  } = useCRUDUserPopupProviderContext()

  const {
    actions: {
      reassignCCResponsible,
      setSelectedCC,
      updateReassignmentRequest,
      unassignRequest
    },
    state: { reassignmentRequest, form, isReassign }
  } = useCRUDUserContext()

  useEffect(() => {
    if (reassignmentRequest && visible) {
      const data = formatReassignDataToForm(reassignmentRequest)
      form.setFieldsValue({
        ...data,
        isAssignAnotherChecked: !!data.assignee
      })
    }
  }, [visible, reassignmentRequest, form])

  const onSubmit = useCallback(
    async (values: TCCRelatedItemReassign) => {
      if (selectedCC?.uuid) {
        const data = formatReassignDataToRequest(values, isReassign)
        if (reassignmentRequest?.uuid) {
          await updateReassignmentRequest(selectedCC.uuid, data)
        } else if (isReassign) {
          await reassignCCResponsible(selectedCC.uuid, data)
        } else {
          await unassignRequest(selectedCC.uuid, data)
        }
      }
    },
    [
      isReassign,
      reassignCCResponsible,
      reassignmentRequest?.uuid,
      selectedCC?.uuid,
      unassignRequest,
      updateReassignmentRequest
    ]
  )

  const onCancel = useCallback(() => {
    close()
    setSelectedCC(null)
    form.resetFields()
  }, [close, form, setSelectedCC])

  const submitBtnTxt = useMemo(() => {
    if (reassignmentRequest) {
      return BTN_TXT.SAVE
    }
    return isReassign ? BTN_TXT.REASSIGN : BTN_TXT.UNASSIGN
  }, [isReassign, reassignmentRequest])

  return (
    <Popup
      visible={visible}
      onCancel={onCancel}
      className="manage-assignment-popup"
      width={442}
      title={
        <div className="column gap-12">
          {title}
          <Typography.Body2 className="manage-assignment-popup__subtitle">
            Remove {selectedCC?.name} from {userName} contract categories, or
            assign it to another user.
          </Typography.Body2>
        </div>
      }
      footer={
        <>
          <Button onClick={onCancel} upperCase>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            upperCase
            form="manage-category-assignment-form"
            htmlType="submit"
          >
            {submitBtnTxt}
          </Button>
        </>
      }
    >
      <Forms.ManageCategoryAssignment form={form} onSubmit={onSubmit} />
    </Popup>
  )
}
